import React from 'react';
import { ICONS } from 'shared/constants/images';
import { Card } from 'shared/ui/card/card';
import { Icon } from 'shared/ui/icon/icon';
import cls from './rules.module.scss';
import {
  getRuleTextTranslationFile,
  INFO_COMPLETNESS_RULES_TEXT,
} from 'shared/constants/rules-texts';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { getPagePrefix, track } from 'utils/mixpanel';
import { linkifyText } from 'shared/helpers/linkifyText';
import { getRuleTrackName } from 'widgets/score-disclosure/helpers';
import { ExternalLink } from 'shared/ui/external-link/external-link';

interface RuleDescriptionProps {
  ruleTitle: string;
  parentRuleTitle?: string;
  trackName?: string;
  productNumber?: number;
}

export const RuleDescription: React.FC<RuleDescriptionProps> = ({
  ruleTitle,
  parentRuleTitle,
  trackName,
  productNumber,
}) => {
  const { isTrial } = useCachedSelectedProject();
  const { t } = useTranslation();
  const resolvedRuleKey =
    ruleTitle === 'schema_readiness'
      ? `${parentRuleTitle}_${ruleTitle}`
      : ruleTitle;
  const ruleText = INFO_COMPLETNESS_RULES_TEXT[resolvedRuleKey];
  if (!ruleText) {
    return null;
  }
  const translationFile = getRuleTextTranslationFile(resolvedRuleKey);
  const { sourceLinkTop, sourceLinkBot } = ruleText;
  const subTrack = getRuleTrackName(ruleTitle);
  const pagePrefix = getPagePrefix(window.location.pathname);
  const trackLinkClick = (link?: string) => {
    if (trackName) {
      track(`AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-SOURCE_LINK-CLICKED`, {
        is_trial: isTrial,
        type: trackName,
        sub_type: subTrack,
        card_type: 'Rule Description',
        link: link,
      });
    } else {
      track(`AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-SOURCE_LINK-CLICKED`, {
        is_trial: isTrial,
        type: subTrack,
        card_type: 'Rule Description',
        link: link,
        ...(productNumber && { product: productNumber }),
      });
    }
  };
  return (
    <div>
      <p className={cls.intro}>{t(ruleText.description, translationFile)}</p>
      <div className={cls.cards}>
        <Card className={`${cls.infoCard} ${cls.dark}`}>
          <div className={cls.header}>
            <h4>{t('details')}</h4>
          </div>
          <ul style={{ listStyle: `url(${ICONS.listStar})` }}>
            {ruleText.details.map(d => (
              <li key={d}>{t(d, translationFile)}</li>
            ))}
          </ul>
        </Card>
        <Card className={`${cls.infoCard} ${cls.light}`}>
          <div className={cls.header}>
            <h4>{t('sources')}</h4>
            <Icon pathToIcon={ICONS.book} />
          </div>
          {sourceLinkTop.link ? (
            <ExternalLink
              href={sourceLinkTop.link}
              onClick={() => trackLinkClick(sourceLinkTop.link)}
            >
              {t(sourceLinkTop.text, translationFile)}
            </ExternalLink>
          ) : (
            <p>{t(sourceLinkTop.text, translationFile)}</p>
          )}
          <ul style={{ listStyle: `url(${ICONS.listStar})` }}>
            {ruleText.sources.map(s => (
              <li key={s} style={{ wordBreak: 'break-word' }}>
                {linkifyText(t(s, translationFile))}
              </li>
            ))}
          </ul>
          {sourceLinkBot?.link ? (
            <ExternalLink
              href={sourceLinkBot.link}
              onClick={() => trackLinkClick(sourceLinkBot.link)}
            >
              {t(sourceLinkBot.text, translationFile)}
            </ExternalLink>
          ) : (
            <p>
              {sourceLinkBot?.text
                ? t(sourceLinkBot.text, translationFile)
                : ''}
            </p>
          )}
          {ruleText.sourcesMore && (
            <ul style={{ listStyle: `url(${ICONS.listStar})` }}>
              {ruleText.sourcesMore.map(s => (
                <li key={s} style={{ wordBreak: 'break-word' }}>
                  {linkifyText(t(s, translationFile))}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};
